import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowSquareOut, FolderSimple, MagnifyingGlass, Receipt, UploadSimple } from '@phosphor-icons/react';
import { ImmoButton, LeerZustand, SegmentedControl } from '../../../../../components/immobilien/ui';
import { supabase } from '../../../../../lib/supabase/client';
import { dokumentOeffnenUrl } from '../../../../../lib/dokumente/dokumentLink';
import { deDatum } from '../../../../../lib/immobilien/objektDetail';
import { normalisiereSuche } from '../../../../../lib/immobilien/suche';
import { useObjektSeite } from '../ObjektSeitenKontext';

function endung(name: string | null, typ: string | null): string {
  const ausName = (name ?? '').split('.').pop() ?? '';
  const roh = ausName && ausName.length <= 5 && ausName !== name ? ausName : (typ ?? '').split('/').pop() ?? '';
  return (roh || 'DOK').slice(0, 4).toUpperCase();
}

/**
 * Panel „Dokumente“: die neuesten 50 Dokumente des Objekts, Suche und Kategorien.
 * Klick wie bisher: Gehört das Dokument zu einer Rechnung des Objekts, öffnet sich die Rechnung
 * (Ticket #428), sonst die Datei. Hochladen und alle Dokumente: Dokumentenbereich.
 */
export function DokumentePanel() {
  const { daten } = useObjektSeite();
  const navigate = useNavigate();
  const [suche, setSuche] = useState('');
  const [kategorie, setKategorie] = useState('alle');
  const [rechnungJeDokument, setRechnungJeDokument] = useState<Map<string, string>>(new Map());
  const objektId = daten.objekt.id;

  useEffect(() => {
    let aktiv = true;
    supabase.from('rechnungen').select('id, dokument_id').eq('objekt_id', objektId).not('dokument_id', 'is', null)
      .then(({ data }) => {
        if (!aktiv) return;
        setRechnungJeDokument(new Map(((data ?? []) as { id: string; dokument_id: string }[]).map(r => [r.dokument_id, r.id])));
      });
    return () => { aktiv = false; };
  }, [objektId]);

  const kategorien = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of daten.dokumente) { const k = (d.kategorie ?? '').trim() || 'Ohne Kategorie'; m.set(k, (m.get(k) ?? 0) + 1); }
    return [...m.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5);
  }, [daten.dokumente]);

  const q = normalisiereSuche(suche);
  const sichtbar = daten.dokumente.filter(d =>
    (kategorie === 'alle' || ((d.kategorie ?? '').trim() || 'Ohne Kategorie') === kategorie)
    && (!q || normalisiereSuche(`${d.titel ?? ''} ${d.datei_name ?? ''} ${d.kategorie ?? ''}`).includes(q)));

  const zumBereich = () => navigate(`/admin/dokumente?objekt=${encodeURIComponent(objektId)}`);

  if (daten.dokumente.length === 0) {
    return (
      <LeerZustand icon={FolderSimple} titel="Noch keine Dokumente" text="Dokumente werden im Dokumentenbereich hochgeladen oder per E-Mail automatisch abgelegt.">
        <ImmoButton art="akzent" icon={UploadSimple} onClick={zumBereich}>Im Dokumentenbereich hochladen</ImmoButton>
      </LeerZustand>
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
        <label className="flex items-center" style={{ flex: '1 1 220px', height: 44, borderRadius: 22, gap: 8, padding: '0 14px', background: 'var(--fill2)' }}>
          <MagnifyingGlass size={16} color="var(--ink3)" aria-hidden />
          <input value={suche} onChange={e => setSuche(e.target.value)} placeholder="Dokumente durchsuchen" aria-label="Dokumente durchsuchen"
            className="flex-1 min-w-0" style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 14, color: 'var(--ink)' }} />
        </label>
        <ImmoButton art="akzent" groesse={44} pille={false} icon={UploadSimple} onClick={zumBereich}>Hochladen</ImmoButton>
      </div>
      <div className="immo-ohne-scrollbar overflow-x-auto">
        <SegmentedControl hoehe={30} ariaLabel="Kategorie" wert={kategorie} onChange={setKategorie}
          segmente={[{ key: 'alle', label: 'Alle', anzahl: daten.dokumente.length }, ...kategorien.map(([k, n]) => ({ key: k, label: k, anzahl: n }))]} />
      </div>
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))', gap: 10 }}>
        {sichtbar.map(d => {
          const rechnungId = rechnungJeDokument.get(d.id);
          const url = dokumentOeffnenUrl(d);
          const oeffnen = rechnungId ? () => navigate(`/admin/rechnungen/${encodeURIComponent(rechnungId)}`)
            : url ? () => window.open(url, '_blank', 'noopener,noreferrer') : undefined;
          return (
            <button key={d.id} type="button" onClick={oeffnen} disabled={!oeffnen}
              title={rechnungId ? 'Zur Rechnung' : url ? 'Datei öffnen' : 'Nur im Dokumentenbereich erreichbar'}
              className={`immo-innen flex items-center text-left ${oeffnen ? 'immo-hover-flaeche cursor-pointer' : 'cursor-default'}`}
              style={{ borderRadius: 20, padding: 14, gap: 12, border: 'none', color: 'var(--ink)', opacity: oeffnen ? 1 : 0.6 }}>
              <span className="flex items-end justify-center shrink-0" style={{ width: 42, height: 52, borderRadius: 9, paddingBottom: 6, background: 'linear-gradient(180deg,#f4f4f6,#d9d9de)', boxShadow: '0 4px 10px var(--shade)' }}>
                <span style={{ fontSize: 8, fontWeight: 800, color: 'var(--bad)' }}>{endung(d.datei_name, d.datei_typ)}</span>
              </span>
              <span className="flex flex-col flex-1 min-w-0" style={{ gap: 2 }}>
                <span className="truncate" style={{ fontSize: 14, fontWeight: 800 }}>{d.titel || d.datei_name || 'Dokument'}</span>
                <span className="truncate" style={{ fontSize: 11, color: 'var(--ink3)' }}>{(d.kategorie ?? '').trim() || 'Ohne Kategorie'} · {deDatum(d.created_at)}</span>
              </span>
              {rechnungId ? <Receipt size={16} color="var(--acc)" /> : oeffnen ? <ArrowSquareOut size={16} color="var(--ink3)" /> : null}
            </button>
          );
        })}
      </div>
      {sichtbar.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Keine Dokumente gefunden.</span>}
      <div className="flex items-center justify-between flex-wrap" style={{ gap: 10, fontSize: 12, color: 'var(--ink3)' }}>
        <span>{daten.dokumente.length < daten.dokumenteAnzahl ? `Die neuesten ${daten.dokumente.length} von ${daten.dokumenteAnzahl} Dokumenten` : `${daten.dokumenteAnzahl} Dokumente`}</span>
        <ImmoButton groesse={36} icon={FolderSimple} onClick={zumBereich}>Alle im Dokumentenbereich</ImmoButton>
      </div>
    </>
  );
}
